import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Animated, type GestureResponderEvent, Pressable } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import TooltipWrapper from "../TooltipWrapper";

export type FilterSectionHeaderStatus =
	| "checked"
	| "unchecked"
	| "indeterminate";

type Props = {
	title: string;
	status: FilterSectionHeaderStatus;
	selectedCount: number;
	expanded: boolean;
	disabled?: boolean;
	onToggle: () => void;
	onToggleExpanded: () => void;
};

const getStatusIcon = (status: FilterSectionHeaderStatus) => {
	if (status === "checked") {
		return "checkbox-marked";
	}
	if (status === "indeterminate") {
		return "minus-box";
	}
	return "checkbox-blank-outline";
};

export const FilterSectionHeader = ({
	title,
	status,
	selectedCount,
	expanded,
	disabled,
	onToggle,
	onToggleExpanded,
}: Props) => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const rotation = useRef(new Animated.Value(expanded ? 1 : 0)).current;
	const iconColor =
		status === "unchecked" ? theme.colors.outline : theme.colors.primary;
	const actionLabel =
		status === "checked"
			? t("common.filters.deselectAll")
			: t("common.filters.selectAll");
	const expansionLabel = t(
		expanded
			? "common.filters.collapseSection"
			: "common.filters.expandSection",
		{ section: title },
	);
	const handleExpansionPress = (event: GestureResponderEvent) => {
		event.stopPropagation();
		onToggleExpanded();
	};

	useEffect(() => {
		const animation = Animated.timing(rotation, {
			toValue: expanded ? 1 : 0,
			duration: 200,
			useNativeDriver: true,
		});
		animation.start();
		return () => animation.stop();
	}, [expanded, rotation]);

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			marginHorizontal={-10}
			backgroundColor={theme.colors.elevation.level2}
			borderRadius={theme.roundness * 2}
		>
			<Pressable
				onPress={onToggle}
				disabled={disabled}
				accessibilityRole="checkbox"
				accessibilityLabel={`${title}. ${actionLabel}`}
				aria-checked={
					status === "indeterminate" ? "mixed" : status === "checked"
				}
				accessibilityState={{
					checked: status === "indeterminate" ? "mixed" : status === "checked",
					disabled,
				}}
				style={{
					opacity: disabled ? 0.5 : 1,
					flex: 1,
					alignSelf: "stretch",
					justifyContent: "center",
				}}
			>
				<Box
					flexDirection="row"
					alignItems="center"
					gap={12}
					paddingLeft={10}
					paddingVertical={2}
				>
					<TooltipWrapper
						title={actionLabel}
						enterTouchDelay={750}
						style={{ cursor: "pointer" }}
					>
						<AppIcon name={getStatusIcon(status)} size={24} color={iconColor} />
					</TooltipWrapper>
					<Text
						variant="titleSmall"
						style={{ color: theme.colors.onSurface, flex: 1 }}
					>
						{title}
					</Text>
					<Box
						paddingHorizontal={8}
						paddingVertical={4}
						borderRadius={999}
						backgroundColor={theme.colors.surfaceVariant}
					>
						<Text
							variant="labelMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{t("common.filters.selectedCount", {
								count: selectedCount,
							})}
						</Text>
					</Box>
				</Box>
			</Pressable>
			<Pressable
				onPress={handleExpansionPress}
				hitSlop={8}
				accessibilityRole="button"
				accessibilityLabel={expansionLabel}
				accessibilityState={{ expanded }}
				style={{ paddingLeft: 6, paddingRight: 12, paddingVertical: 6 }}
			>
				<Animated.View
					style={{
						transform: [
							{
								rotate: rotation.interpolate({
									inputRange: [0, 1],
									outputRange: ["0deg", "180deg"],
								}),
							},
						],
					}}
				>
					<AppIcon
						name="chevron-down"
						size={24}
						color={theme.colors.onSurface}
					/>
				</Animated.View>
			</Pressable>
		</Box>
	);
};
